/**
 * Firebase Client SDK — Browser-side initialization
 * Used for: signInWithCustomToken, auth state management
 *
 * Environment variables (VITE_ prefix for client access):
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID
 *   VITE_FIREBASE_STORAGE_BUCKET
 *   VITE_FIREBASE_MESSAGING_SENDER_ID
 *   VITE_FIREBASE_APP_ID
 */
import { initializeApp, getApps, getApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  type Auth,
  type User as FirebaseUser,
} from "firebase/auth";

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

function getFirebaseConfig() {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
  };
}

/**
 * Check if Firebase is configured (API key is present)
 */
export function isFirebaseConfigured(): boolean {
  const config = getFirebaseConfig();
  return !!(config.apiKey && config.projectId);
}

export function getFirebaseApp(): FirebaseApp | null {
  if (!isFirebaseConfigured()) {
    console.warn("[Firebase] Not configured — missing VITE_FIREBASE_API_KEY or VITE_FIREBASE_PROJECT_ID");
    return null;
  }
  if (!_app) {
    // Reuse the default app if it already exists (e.g., from HMR or another module)
    _app = getApps().length > 0 ? getApp() : initializeApp(getFirebaseConfig());
  }
  return _app;
}

export function getFirebaseAuth(): Auth | null {
  const app = getFirebaseApp();
  if (!app) return null;
  if (!_auth) {
    _auth = getAuth(app);
  }
  return _auth;
}

/**
 * Sign in with Firebase Custom Token (received from server after LINE Login)
 */
export async function firebaseSignInWithCustomToken(customToken: string): Promise<FirebaseUser> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase not configured");
  const credential = await signInWithCustomToken(auth, customToken);
  return credential.user;
}

/**
 * Sign out from Firebase
 */
export async function firebaseSignOut(): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) return;
  await signOut(auth);
}

/**
 * Get the current Firebase ID token for API authentication
 * Returns null if not signed in or Firebase not configured
 */
export async function getFirebaseIdToken(): Promise<string | null> {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/**
 * Subscribe to Firebase auth state changes
 * Returns an unsubscribe function. If Firebase is not configured,
 * immediately calls back with null and returns a no-op unsubscribe.
 */
export function onFirebaseAuthStateChanged(
  callback: (user: FirebaseUser | null) => void
): () => void {
  const auth = getFirebaseAuth();
  if (!auth) {
    // Not configured — treat as "no user"
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}

/**
 * Sign up with email and password
 */
export async function firebaseSignUpWithEmail(
  email: string,
  password: string
): Promise<FirebaseUser> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase not configured");
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  return credential.user;
}

/**
 * Sign in with email and password
 */
export async function firebaseSignInWithEmail(
  email: string,
  password: string
): Promise<FirebaseUser> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase not configured");
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
}

export type { FirebaseUser };


/**
 * Exchange Firebase ID token for session cookie
 * This should be called after successful sign-in to establish a server-side session
 */
export async function exchangeIdTokenForSessionCookie(): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase not configured");
  
  const user = auth.currentUser;
  if (!user) throw new Error("No user signed in");
  
  try {
    // Get fresh ID token
    const idToken = await user.getIdToken(true);
    
    // Send to backend to create session cookie
    const response = await fetch("/api/sessionLogin", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ idToken }),
      credentials: "include", // Include cookies in request
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create session");
    }
    
    const data = await response.json();
    console.log("[Firebase] Session cookie created successfully", data);
  } catch (error) {
    console.error("[Firebase] Failed to exchange token for session:", error);
    throw error;
  }
}

/**
 * Clear session cookie by calling logout endpoint
 */
export async function clearSessionCookie(): Promise<void> {
  try {
    await fetch("/api/sessionLogout", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    console.log("[Firebase] Session cookie cleared");
  } catch (error) {
    console.error("[Firebase] Failed to clear session:", error);
  }
}
